import { Toast as ToastPrimitive } from "@base-ui/react/toast"

import Icon from "#React/ui/Icon/Icon"
import cn from "#Utils/cn"

type ToastVariant = "default" | "success" | "error" | "warning" | "info"

type ToastPosition =
  | "top-right"
  | "top-left"
  | "top-center"
  | "bottom-right"
  | "bottom-left"
  | "bottom-center"

interface ToastData {
  variant?: ToastVariant
  icon?: string
}

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  default: "bg-surface-raised border-edge-default",
  success: "bg-surface-success border-edge-success",
  error: "bg-surface-error border-edge-error",
  warning: "bg-surface-warning border-edge-warning",
  info: "bg-surface-info border-edge-info",
}

const POSITION_CLASSES: Record<ToastPosition, string> = {
  "top-right": "top-4 right-4 items-end",
  "top-left": "top-4 left-4 items-start",
  "top-center": "top-4 left-1/2 -translate-x-1/2 items-center",
  "bottom-right": "bottom-4 right-4 items-end",
  "bottom-left": "bottom-4 left-4 items-start",
  "bottom-center": "bottom-4 left-1/2 -translate-x-1/2 items-center",
}

type ToastProviderProps = ToastPrimitive.Provider.Props & {
  position?: ToastPosition
}

const ToastProvider = ({ children, position = "top-right", ...props }: ToastProviderProps) => {
  return (
    <ToastPrimitive.Provider {...props}>
      {children}
      <ToastPrimitive.Portal>
        <ToastPrimitive.Viewport
          className={cn(
            "pointer-events-none fixed z-[9999] flex flex-col gap-2",
            POSITION_CLASSES[position],
          )}
          data-testid={`toast-viewport-${position}`}
        >
          <ToastList />
        </ToastPrimitive.Viewport>
      </ToastPrimitive.Portal>
    </ToastPrimitive.Provider>
  )
}

const ToastList = () => {
  const { toasts } = ToastPrimitive.useToastManager<ToastData>()
  return toasts.map((toast) => {
    const variant = toast.data?.variant ?? "default"
    const icon = toast.data?.icon

    return (
      <ToastPrimitive.Root
        key={toast.id}
        toast={toast}
        className={cn(
          "pointer-events-auto relative w-72 rounded-md border p-3 pr-8 shadow-md",
          VARIANT_CLASSES[variant],
        )}
      >
        <ToastPrimitive.Content className="flex flex-col items-start gap-1">
          {(icon || toast.title) && (
            <div className="flex min-h-5 items-center gap-1 text-content-default">
              {icon && (
                <span className="flex h-5 shrink-0 items-center">
                  <Icon name={icon} />
                </span>
              )}
              {toast.title && <ToastPrimitive.Title className="text-sm leading-5 font-semibold" />}
            </div>
          )}
          {toast.description && (
            <ToastPrimitive.Description className="text-sm text-content-default" />
          )}
          {toast.actionProps && (
            <ToastPrimitive.Action className="mt-1 cursor-pointer text-sm font-medium text-link" />
          )}
        </ToastPrimitive.Content>
        <ToastPrimitive.Close
          className="absolute top-0.5 right-2 cursor-pointer opacity-60 hover:opacity-100"
          aria-label="Dismiss toast"
        >
          <Icon variant="close" size="xs" />
        </ToastPrimitive.Close>
      </ToastPrimitive.Root>
    )
  })
}

const useToast = ToastPrimitive.useToastManager<ToastData>

export { ToastList, ToastProvider, useToast }
export type { ToastData, ToastPosition, ToastProviderProps, ToastVariant }
export default ToastProvider
